import React, { useEffect, useMemo, useState } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import { ExportHubAPI, FieldOptionField } from 'api/ExportHubAPI';

/**
 * Searchable select that matches the app's tariff-tool Autocomplete styling and
 * (optionally) lets the user create a new option inline. Created options persist
 * to the database per team + field (via ExportFieldOption), so they remain
 * available to everyone on the team on every later visit.
 */

const filter = createFilterOptions<string>();
// Sentinel prefix marking the synthetic "Create …" row so we can tell a freshly
// created value apart from an existing option in onChange.
const CREATE = ' create:';

interface Props {
  value: string;
  onChange: (_value: string) => void;
  options: string[];
  placeholder?: string;
  /** Allow creating new options (default true). */
  allowCreate?: boolean;
  /**
   * When set (with groupId), created options persist server-side under this
   * field and are loaded for the team on mount.
   */
  field?: FieldOptionField;
  groupId?: string;
  /** Optional secondary text shown after an option in the dropdown. */
  describe?: (_opt: string) => string | undefined;
  ariaLabel?: string;
  /** Hide the clear (x) button — for required, never-empty fields like Status. */
  disableClearable?: boolean;
}

const CreatableSelect: React.FC<Props> = ({
  value,
  onChange,
  options,
  placeholder,
  allowCreate = true,
  field,
  groupId,
  describe,
  ariaLabel,
  disableClearable = false,
}) => {
  // Team-created options for this field, loaded from + saved to the database.
  const [stored, setStored] = useState<string[]>([]);
  const persists = !!(allowCreate && field && groupId);

  useEffect(() => {
    if (!persists || !field || !groupId) return;
    let active = true;
    ExportHubAPI.getFieldOptions(groupId, field)
      .then((opts) => {
        if (active) setStored(opts);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [persists, field, groupId]);

  // Base options + previously-created options, de-duped. For creatable
  // (freeSolo) fields the live `value` is deliberately excluded: it's mirrored
  // from the typed input (see onInputChange), and including it would suppress
  // the "+ Create …" row and duplicate the entry. Non-creatable values already
  // live in `options`, so `value` is added there only as a safety net.
  const merged = useMemo(() => {
    const seen = new Set<string>();
    const out: string[] = [];
    const source = allowCreate
      ? [...options, ...stored]
      : [...options, ...stored, value];
    source.forEach((o) => {
      const t = (o || '').trim();
      if (t && !seen.has(t.toLowerCase())) {
        seen.add(t.toLowerCase());
        out.push(t);
      }
    });
    return out;
  }, [options, stored, value, allowCreate]);

  const commit = async (raw: string) => {
    const t = raw.trim();
    if (!t) {
      onChange('');
      return;
    }
    onChange(t);
    // Check against the real known sets (base + team-stored), NOT `merged` —
    // `merged` includes the live `value`, so a freshly-typed value would look
    // "known" and never persist once we mirror typed text into `value`.
    const known = [...options, ...stored].some(
      (o) => o.toLowerCase() === t.toLowerCase(),
    );
    if (!known && persists && field && groupId) {
      // Optimistically show it, then persist (server returns the canonical list).
      setStored((s) => [...s, t]);
      try {
        const next = await ExportHubAPI.addFieldOption({
          group_id: groupId,
          field: field,
          value: t,
        });
        setStored(next);
      } catch {
        /* keep the optimistic value; it can be retried on next create */
      }
    }
  };

  return (
    <Autocomplete
      freeSolo={allowCreate}
      disableClearable={disableClearable}
      fullWidth
      selectOnFocus
      handleHomeEndKeys
      value={value || null}
      options={merged}
      aria-label={ariaLabel}
      getOptionLabel={(o) =>
        typeof o === 'string'
          ? o.startsWith(CREATE)
            ? o.slice(CREATE.length)
            : o
          : ''
      }
      filterOptions={(opts, params) => {
        const filtered = filter(opts as string[], params);
        const input = params.inputValue.trim();
        const exists = (opts as string[]).some(
          (o) => o.toLowerCase() === input.toLowerCase(),
        );
        if (allowCreate && input !== '' && !exists) {
          filtered.push(`${CREATE}${input}`);
        }
        return filtered;
      }}
      renderOption={(props, o) => {
        if (typeof o === 'string' && o.startsWith(CREATE)) {
          return (
            <li {...props} key={o}>
              <span className='eh-creatable-add'>
                + Create &ldquo;{o.slice(CREATE.length)}&rdquo;
              </span>
            </li>
          );
        }
        const desc = describe?.(o as string);
        return (
          <li {...props} key={o as string}>
            <span>{o as string}</span>
            {desc && <span className='eh-creatable-desc'>&nbsp;— {desc}</span>}
          </li>
        );
      }}
      onChange={(_e, newVal) => {
        if (newVal === null || newVal === undefined) {
          onChange('');
          return;
        }
        const v = newVal as string;
        commit(v.startsWith(CREATE) ? v.slice(CREATE.length) : v);
      }}
      onInputChange={(_e, newInput, reason) => {
        // Creatable fields: mirror typed text into the parent form as the user
        // types, so a value that was typed but never explicitly selected /
        // "created" (e.g. the user types then clicks Continue) still counts and
        // passes required-field checks. Ignore programmatic 'reset' syncs so we
        // don't clobber a committed value.
        if (allowCreate && reason === 'input') {
          onChange(newInput);
        }
      }}
      onBlur={() => {
        // Persist a typed-but-not-selected value to the team option list when
        // focus leaves (the form value is already in sync via onInputChange).
        const t = (value || '').trim();
        if (allowCreate && t) commit(t);
      }}
      componentsProps={{
        paper: {
          sx: {
            background: 'var(--bg-colors)',
            color: 'var(--text-primary-color)',
            border: '1px solid var(--es-soft-border)',
            fontFamily: 'Inter, sans-serif',
            fontSize: '14px',
          },
        },
        popper: {
          sx: {
            zIndex: 1500,
            '& .MuiAutocomplete-option': {
              fontSize: '14px',
              color: 'var(--text-primary-color)',
            },
            '& .MuiAutocomplete-option.Mui-focused': {
              background: 'var(--bg-hover) !important',
            },
            '& .MuiAutocomplete-option[aria-selected="true"]': {
              background: 'var(--bg-surface) !important',
            },
            '& .MuiAutocomplete-noOptions': {
              color: 'var(--text-tertiary)',
            },
          },
        },
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: '8px',
          background: 'var(--bg-surface)',
          padding: '3px 8px',
          minHeight: '40px',
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          color: 'var(--text-primary-color)',
          '& fieldset': { borderColor: 'var(--es-soft-border)' },
          '&:hover fieldset': { borderColor: 'var(--action-color)' },
          '&.Mui-focused fieldset': {
            borderColor: 'var(--action-color)',
            borderWidth: '1px',
          },
        },
        '& .MuiAutocomplete-input': {
          color: 'var(--text-primary-color)',
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
        },
        '& .MuiSvgIcon-root': { color: 'var(--text-secondary)' },
      }}
      renderInput={(params) => (
        <TextField {...params} placeholder={placeholder} />
      )}
    />
  );
};

export default CreatableSelect;
